<h2 mat-dialog-title>
  <mat-icon class="title-icon">help_outline</mat-icon>
  About payout rules
</h2>

<mat-dialog-content data-testid="payout-rules-help-content">

  <p class="lead">
    Every game runs the same way: half of each ticket sale goes to the prize pot, the rest to the groups
    after fees. That never changes and is not configurable.
  </p>
  <p class="lead">
    What differs between organizations is only <strong>how the money is described at payout time</strong> —
    who gets which cheque. These two settings replace the per-organization spreadsheets that used to
    describe it. Nothing here changes what anyone earns.
  </p>

  <h3>Host admin fee</h3>
  <p>
    An administration fee is already collected on every payment, at the rate set in the game's payment
    settings. Some organizations take a share of it and some do not. Turning this on shows that fee and who
    receives it.
  </p>
  <p>
    <strong>The shares are shares of that fee, not of sales.</strong> They must total exactly 100% because
    the fee is one fixed amount being divided, not several separate rates. So on a game with a 1% fee where
    two parties each take 0.5% of every payment, each share is <strong>50</strong>:
  </p>
  <pre class="example">1% administration fee, collected on every payment
  Sport PEI                  share 50   = 0.5% of every payment
  North River Minor Hockey   share 50   = 0.5% of every payment
                           ─────────
                           share 100   = the whole 1% fee</pre>
  <p>
    Entering 0.5 and 0.5 gives a total of 1% of the fee, which the form blocks: it would leave 99% of the
    fee unaccounted for.
  </p>
  <p>
    100% never means the host takes everything and the charities take nothing. The charities' share is
    worked out <em>after</em> this fee has already come off each payment, so the report's charity figures
    are the same whether the fee is shown here or not — which is why setting the shares to 100% changes
    nothing in the summary except that the fee and its recipients now appear. If the period's fee were
    $1,000.01, split 50/50:
  </p>
  <pre class="example">Sport PEI                    500.01
North River Minor Hockey     500.00
                           ────────
                           1,000.01   the fee, exactly</pre>
  <p class="aside">
    The odd cent goes to whoever is listed first, so the same fee always splits the same way — re-running
    last month's report never moves it.
  </p>

  <h3>Amount to pay the organization</h3>
  <p>
    This is the payment Causable sends the host organization for the period. It varies most between
    organizations, so you choose what it is made of: tick the figures and set each to add or subtract.
  </p>

  <p class="example-label">The organization distributes to its own groups:</p>
  <pre class="example">+ Group allocations         12,430.05
= Amount to pay             12,430.05</pre>

  <p class="example-label">The organization also pays the winner:</p>
  <pre class="example">+ Group allocations         12,430.05
+ Winner prize               1,500.00
= Amount to pay             13,930.05</pre>

  <p class="example-label">Everything except Causable's fees:</p>
  <pre class="example">+ Gross sales               25,000.00
− Causable fees               −625.00
− Causable fees tax            −93.75
= Amount to pay             24,281.25</pre>

  <p class="aside">
    The report prints these lines with the total underneath, so the amount always shows its own arithmetic
    rather than appearing as a number you have to trust.
  </p>

  <h3>A note on wording</h3>
  <ul>
    <li><strong>Host organization</strong> — the organization running the game (UPEI, Sport PEI), not a venue.</li>
    <li><strong>Group allocations</strong> — what the charities or groups earned in the period, after fees,
      refunds and disputes. The same figure the per-charity breakdown adds up to.</li>
  </ul>

</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-flat-button color="primary" mat-dialog-close data-testid="payout-rules-help-close">Close</button>
</mat-dialog-actions>
